<script setup lang="ts">
import type { SavedFolder } from "@gasparigit/omnia-sdk";
import { ChevronRight, FolderPlusIcon } from "lucide-vue-next";
import { processError } from "~/lib/utils/errors";

const omniaSdk = useOmniaSdk();
defineProps<{
  title?: string;
}>();

const selectedSavedFolderId = ref<number | null>(null);

const {
  data: savedFolders,
  refresh: refreshSavedFolders,
  status: savedFoldersStatus,
} = useAsyncData(`saved-folders-${useId()}`, async () => {
  try {
    if (selectedSavedFolderId.value) {
      return await omniaSdk.v1.savedFolders.get(selectedSavedFolderId.value);
    }

    return await omniaSdk.v1.savedFolders.list({ pagination: "none" });
  } catch (error) {
    processError(error);
  }
});

const allSavedFolders = computed(() => {
  if ("data" in savedFolders.value) return savedFolders.value.data;
  return savedFolders.value.saved_folders;
});

const folderBreadcrumbs = computed(() => {
  if ("data" in savedFolders.value) return null;

  const folders = [];
  let currentFolder = savedFolders.value.saved_folder;
  while (currentFolder) {
    folders.unshift(currentFolder);
    currentFolder = currentFolder.saved_folder;
  }

  return folders.map((folder) => ({
    ...folder,
    href: `/myomnia?folder=${folder.id}`,
  }));
});

const selectedFolder = computed(() => {
  if (!selectedSavedFolderId.value || "data" in savedFolders.value) return null;

  return savedFolders.value;
});

function changeFolder(folderId: number | null) {
  selectedSavedFolderId.value = folderId;
  refreshSavedFolders();
}

const savedFolderDialogData = ref({
  opened: false,
  folder: null as SavedFolder | null,
  parentId: null as number | null,
});

const opened = ref<boolean>(false);
const emit = defineEmits(["selected"]);

function selectFolder() {
  emit("selected", selectedFolder.value);
  opened.value = false;
}
</script>

<template>
  <Dialog v-model:open="opened">
    <DialogTrigger as-child>
      <slot name="trigger"></slot>
    </DialogTrigger>
    <DialogScrollContent class="w-screen max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ title || "Sposta" }}</DialogTitle>
        <DialogDescription>Seleziona la cartella di destinazione</DialogDescription>
      </DialogHeader>

      <div class="flex flex-col lg:flex-row items-center justify-between gap-4 w-full mb-1">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink class="cursor-pointer" v-if="selectedFolder" @click.prevent="changeFolder(null)">I tuoi preferiti</BreadcrumbLink>
              <BreadcrumbPage v-else>I tuoi preferiti</BreadcrumbPage>
            </BreadcrumbItem>
            <template v-if="folderBreadcrumbs?.length">
              <template v-for="folder in folderBreadcrumbs" :key="`saved-folder-breadcrumb-${folder.id}`">
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink class="cursor-pointer" @click.prevent="changeFolder(folder.id)">{{ folder.title }}</BreadcrumbLink>
                </BreadcrumbItem>
              </template>
            </template>
            <template v-if="selectedFolder">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{{ selectedFolder.title }}</BreadcrumbPage>
              </BreadcrumbItem>
            </template>
          </BreadcrumbList>
        </Breadcrumb>
        <Button variant="ghost" size="icon" @click="savedFolderDialogData = { opened: true, folder: null, parentId: selectedSavedFolderId }">
          <FolderPlusIcon class="size-4" />
          <span class="sr-only">Nuova cartella</span>
        </Button>
      </div>

      <ScrollArea class="h-60">
        <div class="grid divide-y">
          <template v-if="savedFoldersStatus === 'pending'">
            <Skeleton v-for="_ in Array(3).keys()" class="h-8 mb-1" />
          </template>
          <template v-else-if="!allSavedFolders.length">
            <div class="flex flex-col items-center justify-center h-60 w-full gap-2 text-muted-foreground text-base">
              <IconsFolder class="size-14" />
              <p>Seleziona "{{ selectedFolder ? selectedFolder.title : "I miei preferiti" }}"</p>
            </div>
          </template>
          <template v-else>
            <Button variant="ghost" v-for="folder in allSavedFolders" :key="`saved-folder-${folder.id}`" class="rounded-none flex gap-2 py-2 items-center" @click="changeFolder(folder.id)">
              <IconsFolder />
              <span class="flex-1 text-left">{{ folder.title }}</span>
              <ChevronRight class="size-4" />
            </Button>
          </template>
        </div>
      </ScrollArea>

      <DialogFooter class="flex justify-end gap-2">
        <DialogClose as-child>
          <Button variant="outline">Annulla</Button>
        </DialogClose>
        <Button @click="selectFolder">Seleziona</Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

  <FrontSavedFolderDialog
    :saved-folder="savedFolderDialogData.folder"
    :parent-id="savedFolderDialogData.parentId"
    v-model:opened="savedFolderDialogData.opened"
    @success="
      () => {
        savedFolderDialogData = { opened: false, folder: null, parentId: null };
        refreshSavedFolders();
      }
    "
  />
</template>
